// ================================================
// MEETING PREPARATION INTELLIGENCE PANEL
// Feature 2 - Meeting prep system with attendee intelligence
// ================================================
import React, { useState } from 'react';
import { 
  ChevronDown, ChevronUp, Users, Brain, Clock, 
  CheckSquare, Square, MessageSquare, Target, AlertCircle,
  Calendar, Video, MapPin, User, TrendingUp
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { UNIQUE_IP_COLORS } from '../focus';
import { executiveSchedule, executiveTeam, decisionLog, actionItems } from '../../data/chiefOfStaffData';

const MeetingPrepPanel = () => {
  const [expandedMeeting, setExpandedMeeting] = useState(null);
  const [checklistState, setChecklistState] = useState({});
  const [postMeetingNotes, setPostMeetingNotes] = useState({});

  // Get attendee details with their current status
  const getAttendeeDetails = (attendeeNames) => {
    return attendeeNames.map(name => {
      const member = executiveTeam.find(m => m.name === name || name.includes(m.name.split(' ')[1]));
      if (member) {
        const pendingItems = actionItems.filter(ai => ai.assignee.id === member.id && ai.status !== 'completed');
        return {
          ...member,
          pendingActionItems: pendingItems.length,
          recentDecisions: decisionLog.filter(d => d.decisionMaker.id === member.id).slice(0, 2)
        };
      }
      return { name, avatar: name.split(' ').map(n => n[0]).join(''), role: 'External', cognitiveLoad: null };
    });
  };

  // Generate suggested talking points based on attendees
  const generateTalkingPoints = (meeting, attendees) => {
    const points = [];
    
    attendees.forEach(att => {
      if (att.cognitiveLoad && att.cognitiveLoad > 80) {
        points.push(`With ${att.name.split(' ')[0]} — consider discussing workload as they are at ${att.cognitiveLoad}% capacity`);
      }
      if (att.pendingActionItems > 0) {
        points.push(`${att.name.split(' ')[0]} has ${att.pendingActionItems} pending action items — may want to check status`);
      }
    });
    
    // Add meeting-specific points
    if (meeting.title.includes('Board')) {
      points.push('Review Q1 performance metrics before presentation');
      points.push('Prepare for questions about expansion timeline');
    }
    if (meeting.title.includes('Soul Food')) {
      points.push('Discuss Nike partnership timeline and next steps');
      points.push('Review community event preparations');
    }
    if (meeting.title.includes('1:1')) {
      points.push('Check on team morale and any support needed');
    }
    
    return points.slice(0, 5);
  };

  const toggleChecklist = (meetingId, item) => {
    setChecklistState(prev => ({
      ...prev,
      [meetingId]: {
        ...prev[meetingId],
        [item]: !prev[meetingId]?.[item]
      }
    }));
  };

  const handlePostMeetingNote = (meetingId, field, value) => {
    setPostMeetingNotes(prev => ({
      ...prev,
      [meetingId]: {
        ...prev[meetingId],
        [field]: value
      }
    }));
  };

  // Tomorrow's meetings (mock)
  const tomorrowMeetings = [
    {
      id: 'tomorrow-1',
      title: 'Regional Directors Quarterly Planning',
      time: '10:00 AM',
      duration: '120 min',
      location: 'Conference Room B',
      type: 'internal',
      attendees: ['Sarah Martinez', 'James Chen', 'Elena Rodriguez'],
      prepNote: 'Each director presenting Q2 plans. Budget discussions expected.',
      agenda: ['Q1 Review', 'Q2 Planning', 'Resource Allocation'],
      checklist: { agendaSent: false, materialsDistributed: false, roomConfirmed: true, executiveBriefed: false }
    }
  ];

  const allMeetings = [
    ...executiveSchedule.map(m => ({ ...m, day: 'Today' })),
    ...tomorrowMeetings.map(m => ({ ...m, day: 'Tomorrow' }))
  ];

  return (
    <div className="p-5 space-y-4">
      {/* Meeting List */}
      {['Today', 'Tomorrow'].map(day => (
        <div key={day} className="space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            {day}'s Meetings
          </h3>
          
          {allMeetings.filter(m => m.day === day).map((meeting) => {
            const isExpanded = expandedMeeting === meeting.id;
            const attendees = getAttendeeDetails(meeting.attendees);
            const talkingPoints = generateTalkingPoints(meeting, attendees);
            const checklist = checklistState[meeting.id] || meeting.checklist || {};
            
            return (
              <div 
                key={meeting.id}
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
              >
                {/* Meeting Header */}
                <button
                  onClick={() => setExpandedMeeting(isExpanded ? null : meeting.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-right" style={{ width: '60px' }}>
                      <span className="text-sm font-bold" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
                        {meeting.time}
                      </span>
                      <p className="text-xs text-gray-500">{meeting.duration}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white">{meeting.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          {meeting.location.includes('Video') || meeting.location.includes('Zoom') 
                            ? <Video className="w-3 h-3" />
                            : <MapPin className="w-3 h-3" />
                          }
                          {meeting.location}
                        </span>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>

                {/* Expanded Meeting Prep */}
                {isExpanded && (
                  <div className="border-t border-white/10 p-4 space-y-5">
                    {/* Attendee Intelligence */}
                    <div className="space-y-3">
                      <h5 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                        <Users className="w-3 h-3" />
                        Attendee Intelligence
                      </h5>
                      <div className="space-y-2">
                        {attendees.map((att, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg"
                            style={{ backgroundColor: '#0D1117' }}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary }}
                              >
                                {att.avatar}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">{att.name}</p>
                                <p className="text-xs text-gray-400">{att.role || att.title}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              {att.cognitiveLoad && (
                                <span 
                                  className="px-2 py-1 rounded"
                                  style={{ 
                                    backgroundColor: att.cognitiveLoad > 80 ? '#EF444420' : att.cognitiveLoad > 60 ? '#F59E0B20' : '#10B98120',
                                    color: att.cognitiveLoad > 80 ? '#EF4444' : att.cognitiveLoad > 60 ? '#F59E0B' : '#10B981'
                                  }}
                                >
                                  {att.cognitiveLoad}% load
                                </span>
                              )}
                              {att.primaryLane && (
                                <span 
                                  className="px-2 py-1 rounded capitalize"
                                  style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary + '20', color: CHIEF_OF_STAFF_COLORS.primaryLight }}
                                >
                                  {att.primaryLane}
                                </span>
                              )}
                              {att.pendingActionItems > 0 && (
                                <span className="text-amber-500 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {att.pendingActionItems} pending
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Suggested Talking Points */}
                    <div className="space-y-3">
                      <h5 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                        <MessageSquare className="w-3 h-3" />
                        Suggested Talking Points
                      </h5>
                      <ul className="space-y-2">
                        {talkingPoints.map((point, idx) => (
                          <li 
                            key={idx}
                            className="flex items-start gap-2 text-sm text-gray-300"
                          >
                            <span style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>→</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Pre-Meeting Checklist */}
                    <div className="space-y-3">
                      <h5 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                        <CheckSquare className="w-3 h-3" />
                        Pre-Meeting Checklist
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'agendaSent', label: 'Agenda sent' },
                          { key: 'materialsDistributed', label: 'Materials distributed' },
                          { key: 'roomConfirmed', label: 'Room confirmed' },
                          { key: 'executiveBriefed', label: 'Executive briefed' }
                        ].map(item => (
                          <button
                            key={item.key}
                            onClick={() => toggleChecklist(meeting.id, item.key)}
                            className="flex items-center gap-2 p-2 rounded-lg text-sm transition-all"
                            style={{ 
                              backgroundColor: checklist[item.key] ? CHIEF_OF_STAFF_COLORS.primary + '20' : '#0D1117',
                              color: checklist[item.key] ? CHIEF_OF_STAFF_COLORS.primary : '#9CA3AF'
                            }}
                          >
                            {checklist[item.key] ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Post-Meeting Section */}
                    <div className="space-y-3 pt-3 border-t border-white/10">
                      <h5 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                        <Target className="w-3 h-3" />
                        Post-Meeting Notes
                      </h5>
                      <div className="space-y-2">
                        <textarea
                          placeholder="Key decisions made..."
                          value={postMeetingNotes[meeting.id]?.decisions || ''}
                          onChange={(e) => handlePostMeetingNote(meeting.id, 'decisions', e.target.value)}
                          className="w-full p-3 rounded-lg text-sm text-white placeholder-gray-500 resize-none"
                          style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
                          rows={2}
                        />
                        <textarea
                          placeholder="Action items assigned..."
                          value={postMeetingNotes[meeting.id]?.actions || ''}
                          onChange={(e) => handlePostMeetingNote(meeting.id, 'actions', e.target.value)}
                          className="w-full p-3 rounded-lg text-sm text-white placeholder-gray-500 resize-none"
                          style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
                          rows={2}
                        />
                        <button 
                          className="w-full py-2 rounded-lg text-sm font-medium transition-all"
                          style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary, color: 'white' }}
                        >
                          Save to Decision Log & Action Tracker
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default MeetingPrepPanel;
